import { useState, useEffect } from 'react';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import { Clock, Plus, Trash2, RefreshCw, X, Users } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LateTracking({ user, onLogout }) {
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [activeTab, setActiveTab] = useState('entries'); // 'entries' or 'summary'
  const [formData, setFormData] = useState({
    employee_email: '',
    date: new Date().toISOString().split('T')[0],
    comments: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [entriesRes, summaryRes, usersRes] = await Promise.all([
        axios.get(`${API}/late-entries`, { headers }),
        axios.get(`${API}/late-entries/summary`, { headers }),
        axios.get(`${API}/users`, { headers }),
      ]);
      
      setEntries(entriesRes.data);
      setSummary(summaryRes.data);
      setEmployees(usersRes.data.filter(u => u.role !== 'Admin'));
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddEntry = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/late-entries`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Late entry added');
      setShowModal(false);
      setFormData({
        employee_email: '',
        date: new Date().toISOString().split('T')[0],
        comments: '',
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add entry');
    }
  };

  const handleDeleteEntry = async (lateId) => {
    if (!window.confirm('Are you sure you want to delete this entry?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/late-entries/${lateId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Entry deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete entry');
    }
  };

  if (loading) {
    return (
      <div className="flex">
        <Sidebar user={user} onLogout={onLogout} />
        <div className="flex-1 flex items-center justify-center min-h-screen">
          <div className="text-center">
            <RefreshCw className="w-8 h-8 animate-spin text-primary mx-auto mb-2" />
            <p className="text-muted-foreground">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar user={user} onLogout={onLogout} />
      
      <div className="flex-1 md:ml-0">
        {/* Header */}
        <div className="bg-white border-b border-border px-4 md:px-8 py-4 md:py-6 pt-16 md:pt-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 md:w-6 md:h-6 text-orange-600" />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground">Late Tracking</h1>
                <p className="text-sm md:text-base text-muted-foreground mt-1">Track employee late arrivals</p>
              </div>
            </div>
            <button
              onClick={() => setShowModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors font-medium"
            >
              <Plus className="w-4 h-4" />
              Mark Late
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="px-4 md:px-8 pt-4">
          <div className="flex gap-2 border-b border-border">
            <button
              onClick={() => setActiveTab('entries')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'entries'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              All Entries ({entries.length})
            </button>
            <button
              onClick={() => setActiveTab('summary')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'summary'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <Users className="w-4 h-4 inline mr-1" />
              Summary
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="p-4 md:p-8">
          {activeTab === 'entries' ? (
            <div className="bg-white rounded-xl border border-border shadow-sm overflow-hidden">
              {/* Desktop Table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Employee</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Comments</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Marked By</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {entries.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="px-6 py-12 text-center text-muted-foreground">
                          No late entries recorded
                        </td>
                      </tr>
                    ) : (
                      entries.map((entry) => (
                        <tr key={entry.late_id} className="hover:bg-muted/50">
                          <td className="px-6 py-4">
                            <div>
                              <p className="font-medium text-sm">{entry.employee_name}</p>
                              <p className="text-xs text-muted-foreground">{entry.employee_email}</p>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm">
                            {new Date(entry.date).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 text-sm text-muted-foreground max-w-xs truncate">
                            {entry.comments || '-'}
                          </td>
                          <td className="px-6 py-4 text-sm text-muted-foreground">
                            {entry.marked_by}
                          </td>
                          <td className="px-6 py-4">
                            <button
                              onClick={() => handleDeleteEntry(entry.late_id)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="block md:hidden divide-y divide-border">
                {entries.length === 0 ? (
                  <div className="p-8 text-center text-muted-foreground">
                    No late entries recorded
                  </div>
                ) : (
                  entries.map((entry) => (
                    <div key={entry.late_id} className="p-4">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className="font-medium text-sm">{entry.employee_name}</p>
                          <p className="text-xs text-muted-foreground">{entry.employee_email}</p>
                        </div>
                        <button
                          onClick={() => handleDeleteEntry(entry.late_id)}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div>
                          <span className="text-muted-foreground">Date: </span>
                          <span className="font-medium">{new Date(entry.date).toLocaleDateString()}</span>
                        </div>
                        {entry.comments && (
                          <div className="col-span-2">
                            <span className="text-muted-foreground">Comments: </span>
                            <span>{entry.comments}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          ) : (
            /* Summary Tab */
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {summary.length === 0 ? (
                <div className="col-span-full text-center py-12 text-muted-foreground">
                  No late entries recorded
                </div>
              ) : (
                summary.map((item) => (
                  <div key={item.employee_email} className="bg-white rounded-xl border border-border p-4 shadow-sm">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{item.employee_name}</p>
                        <p className="text-xs text-muted-foreground">{item.employee_email}</p>
                      </div>
                      <div className={`text-2xl font-bold ${item.late_count >= 5 ? 'text-red-600' : item.late_count >= 3 ? 'text-orange-600' : 'text-foreground'}`}>
                        {item.late_count}
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">late arrivals</p>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      {/* Add Entry Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-heading font-semibold">Mark Late Entry</h2>
              <button onClick={() => setShowModal(false)} className="p-1 hover:bg-muted rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleAddEntry} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Employee</label>
                <select
                  required
                  value={formData.employee_email}
                  onChange={(e) => setFormData({ ...formData, employee_email: e.target.value })}
                  className="w-full px-3 py-2 border border-input rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">Select employee</option>
                  {employees.map((emp) => (
                    <option key={emp.email} value={emp.email}>{emp.name} ({emp.email})</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Date</label>
                <input
                  type="date"
                  required
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="w-full px-3 py-2 border border-input rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Comments (Optional)</label>
                <textarea
                  value={formData.comments}
                  onChange={(e) => setFormData({ ...formData, comments: e.target.value })}
                  placeholder="Add any notes..."
                  className="w-full px-3 py-2 border border-input rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary min-h-[80px]"
                />
              </div>
              
              <div className="flex gap-3 pt-2">
                <button
                  type="submit"
                  className="flex-1 bg-primary text-white rounded-lg py-2 font-medium hover:bg-primary/90 transition-colors"
                >
                  Add Entry
                </button>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border border-input rounded-lg hover:bg-muted transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
